import { of as observableOf, Observable } from 'rxjs';

import { switchMap } from 'rxjs/operators';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { SaveFormService } from '../../services/save-form.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CategoriesService } from '../../services/categories.service';
import { Options } from 'sortablejs';
import { QuestionService } from '../../services/question.service';

import { SurveyService } from '../../services/survey.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AdminSurvey,
  LocalizedQuestionCategory,
  DraggableCategory,
  QuestionType,
  QuestionIconStyle,
  SurveyInfrastructure,
  AdminQuestion,
} from '../../models/questions';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-survey-editor',
  templateUrl: './survey-editor.component.html',
  styleUrls: ['./survey-editor.component.scss'],
})
export class SurveyEditorComponent implements OnInit {
  partOfNameControlName = 'name_';
  partOfDescriptionName = 'description_';
  surveyForm: FormGroup;
  namePropertiesArray: Array<string> = [];
  namePlaceholders: Array<string> = [];
  descriptionPropertiesArray: Array<string> = [];
  descriptionPlaceholders: Array<string> = [];
  survey: AdminSurvey = { duration: '0', name: [], description: [] };
  categories: Array<LocalizedQuestionCategory>;
  surveyDraggableCategories: Array<DraggableCategory> = [];
  oldSurveyDraggableCategories: Array<DraggableCategory> = [];
  menuCategoryQuestionsSortableOptions: Array<Options> = [];
  menuCategoriesSortableOptions: Options = {};
  surveyCategoriesSortableOptions: Options = {};
  isOnSaving: boolean = false;

  private menuCategoriesOptionsName = 'categories-menu-group';
  private surveyCategoriesOptionsName = 'categories-survey-group';
  private readonly durationControlName = 'duration';
  private surveyId: string;

  constructor(
    private formMappingService: SaveFormService,
    private surveyFormBuilder: FormBuilder,
    private categoriesService: CategoriesService,
    private questionsService: QuestionService,
    private surveyService: SurveyService,
    private route: ActivatedRoute,
    private router: Router,
    private ref: ChangeDetectorRef,
    private toastr: ToastrService
  ) {
    this.menuCategoriesSortableOptions = {
      group: {
        name: this.menuCategoriesOptionsName,
        put: false,
        pull: 'clone',
      },
      sort: false,
    };
  }

  ngOnInit() {
    this.surveyId = this.route.snapshot.paramMap.get('id');
    this.createSurveyForm();
    let survey$: Observable<AdminSurvey>;
    this.categoriesService
      .getWithQuestions()
      .pipe(
        switchMap((c) => {
          this.categories = c;
          this.categories.forEach((x) =>
            this.setCategoryQuestionsMenuSortableOptions(x.id)
          );
          survey$ = this.surveyId
            ? this.surveyService.getOne(+this.surveyId)
            : observableOf(this.survey);
          return survey$;
        })
      )
      .subscribe((survey) => this.getSurvey(survey));
  }

  createSurveyForm() {
    this.surveyForm = this.surveyFormBuilder.group({
      duration: this.survey.duration,
    });
  }

  getIconType(answerType: QuestionType): QuestionIconStyle {
    const enumKey = Object.keys(QuestionType).find(
      (k) => QuestionType[k] === answerType
    );
    return QuestionIconStyle[enumKey];
  }

  showHideCategoryQuestions(category: LocalizedQuestionCategory) {
    category.isHidden = !category.isHidden;
    this.ref.detectChanges();
  }

  removeCategoryFromSurvey(categoryId: number) {
    const draggableCategoryIndex = this.surveyDraggableCategories.findIndex(
      (c) => c.category.id === categoryId
    );
    this.surveyDraggableCategories.splice(draggableCategoryIndex, 1);
    this.updateSurveyCategoriesSortableOptions(
      categoryId.toString(),
      this.surveyCategoriesSortableOptions,
      true
    );
    this.oldSurveyDraggableCategories = [...this.surveyDraggableCategories];
    this.ref.detectChanges();
  }

  removeQuestionFromSurvey(event: { questionId: number; categoryId: number }) {
    const draggableCategory = this.surveyDraggableCategories.find(
      (c) => c.category.id === event.categoryId
    );
    const questionToDeleteIndex = draggableCategory.category.questions.findIndex(
      (q) => q.questionId === event.questionId
    );
    if (questionToDeleteIndex === -1) {
      return;
    }
    draggableCategory.category.questions.splice(questionToDeleteIndex, 1);
    if (draggableCategory.category.questions.length === 0) {
      this.removeCategoryFromSurvey(event.categoryId);
      return;
    }
    this.ref.detectChanges();
  }

  changeQuestionMandatory(event: { questionId: number; categoryId: number }) {
    const draggableCategory = this.surveyDraggableCategories.find(
      (c) => c.category.id === event.categoryId
    );
    const question = draggableCategory.category.questions.find(
      (q) => q.questionId === event.questionId
    );
    question.isMandatory = !question.isMandatory;
    this.ref.detectChanges();
  }

  saveSurvey() {
    if (this.isOnSaving) {
      return;
    }
    this.survey.name = this.formMappingService.mapFormvalueToQuestion(
      this.surveyForm.value,
      this.partOfNameControlName
    );
    if (this.survey.name.some((x) => x.name.length === 0)) {
      this.toastr.error('Some Name fields are empty', `Can't save survey`);
      return;
    }
    this.survey.description = this.formMappingService.mapFormvalueToQuestion(
      this.surveyForm.value,
      this.partOfDescriptionName
    );
    this.survey.duration = this.surveyForm.value[this.durationControlName];
    this.survey.infrastructure = this.setQuestionsOrder();
    this.isOnSaving = true;
    if (this.surveyId) {
      this.surveyService.update(this.survey).subscribe((r) => {
        this.isOnSaving = false;
        this.router.navigate(['/admin/survey-list']);
      });
    } else {
      this.surveyService.add(this.survey).subscribe((r) => {
        this.isOnSaving = false;
        this.router.navigate(['/admin/survey-list']);
      });
    }
  }

  private getSurvey(survey: AdminSurvey) {
    this.survey = survey;
    this.initializeSurvey(this.categories);
  }

  private initializeSurvey(categories: Array<LocalizedQuestionCategory>) {
    this.surveyCategoriesSortableOptions = this.setSurveyCategoriesSortableOptions(
      categories
    );
    this.surveyDraggableCategories = this.getSurveyCategories(categories);
    this.oldSurveyDraggableCategories = [...this.surveyDraggableCategories];
    this.mapSurveyToFormGroup();
  }

  private setQuestionsOrder(): Array<SurveyInfrastructure> {
    const questions = this.surveyDraggableCategories.map(
      (c) => c.category.questions
    );
    const joinedQuestions: Array<SurveyInfrastructure> = [];
    questions.forEach((qa) => {
      qa.forEach((q) => joinedQuestions.push(q));
    });
    joinedQuestions.forEach((q, index) => {
      q.order = index + 1;
    });
    return joinedQuestions;
  }

  private getSurveyCategories(
    categories: Array<LocalizedQuestionCategory>
  ): Array<DraggableCategory> {
    const surveyCategoryIds = this.survey.infrastructure
      ? this.survey.infrastructure.map((c) => c.categoryId)
      : [];
    const surveyDraggableCategories: Array<DraggableCategory> = [];
    categories.forEach((x) => {
      const draggableCategory: DraggableCategory = {
        category:
          surveyCategoryIds.findIndex((c) => c === x.id) !== -1
            ? this.mapCategoryInfrastructure(x.id, this.survey)
            : undefined,
        sortableJsOption: this.setSurveyCategoryQuestionsSortableOptions(x.id),
      };
      if (draggableCategory.category) {
        surveyDraggableCategories.push(draggableCategory);
      }
    });
    return surveyDraggableCategories;
  }

  private mapCategoryInfrastructure(
    categoryId: number,
    survey: AdminSurvey
  ): LocalizedQuestionCategory {
    const questionsOfCategory = survey.infrastructure.filter(
      (q) => q.categoryId === categoryId
    );
    this.sortQuestionByOrder(questionsOfCategory);
    const category = { ...this.categories.find((c) => c.id === categoryId) };
    category.questions = questionsOfCategory;
    return category;
  }

  private sortQuestionByOrder(
    surveyInfrastructures: Array<SurveyInfrastructure>
  ) {
    surveyInfrastructures.sort((a, b) => a.order - b.order);
  }

  private setCategoryQuestionsMenuSortableOptions(categoryId: number) {
    this.menuCategoryQuestionsSortableOptions.push({
      group: {
        name: `${categoryId.toString()}`,
        pull: 'clone',
        put: false,
      },
      sort: false,
    });
  }

  private setSurveyCategoriesSortableOptions(
    localizedCategories: Array<LocalizedQuestionCategory>
  ): Options {
    const surveySelectedCategoryIds = this.survey.infrastructure
      ? Array.from(new Set(this.survey.infrastructure.map((x) => x.categoryId)))
      : [];
    const putArray: Array<string> = [];
    localizedCategories.forEach((x) => {
      if (surveySelectedCategoryIds.findIndex((s) => x.id === s) !== -1) {
        return;
      } else {
        putArray.push(x.id.toString());
      }
    });
    return {
      group: {
        name: this.surveyCategoriesOptionsName,
        put: [this.menuCategoriesOptionsName].concat(putArray),
      },
      onAdd: (event: any) => {
        this.addNewCategoryToSurvey(event);
      },
    };
  }

  private setSurveyCategoryQuestionsSortableOptions(
    categoryId: number
  ): Options {
    return {
      group: { name: `${categoryId}`, put: [categoryId.toString()] },
      onAdd: (event: any) => {
        const addedItemId = +event.clone.id;
        const draggableCategory = this.surveyDraggableCategories.find(
          (x) => x.category && x.category.id === categoryId
        );
        this.addQuestionToCategory(addedItemId, draggableCategory.category);
      },
    };
  }

  private addNewCategoryToSurvey(event: any) {
    const category = this.getCategoryFromAddEvent(event);
    if (
      this.oldSurveyDraggableCategories.findIndex(
        (c) => c.category && c.category.id === category.id
      ) !== -1
    ) {
      this.getCategoryQuestions(category);
    } else {
      const newCategoryIndex = this.surveyDraggableCategories.findIndex(
        (c) => c['id'] === category.id
      );
      const categorySurveyJsOptions = this.setSurveyCategoryQuestionsSortableOptions(
        category.id
      );
      this.surveyDraggableCategories = [];
      this.oldSurveyDraggableCategories.splice(newCategoryIndex, 0, {
        category: category,
        sortableJsOption: categorySurveyJsOptions,
      });
    }
    this.updateSurveyCategoriesSortableOptions(
      category.id.toString(),
      this.surveyCategoriesSortableOptions
    );
    this.surveyDraggableCategories = [...this.oldSurveyDraggableCategories];
  }

  private getCategoryQuestions(category: LocalizedQuestionCategory) {
    this.questionsService.getByCategoryId(category.id).subscribe((cq) => {
      cq.forEach((q, i) =>
        category.questions.push(
          this.mapAdminQuestionToSurveyInfrastructure(q, category.id)
        )
      );
      this.mergeSameCategory(category);
      this.surveyDraggableCategories = this.sortCategoryByQuestionsOrder(
        this.surveyDraggableCategories
      );
      this.ref.detectChanges();
    });
  }

  private mergeSameCategory(category: LocalizedQuestionCategory) {
    const data: Array<DraggableCategory> = Object.assign(
      [{}],
      this.oldSurveyDraggableCategories
    );
    data.push({
      category: category,
      sortableJsOption: {},
    });
    const results: Array<DraggableCategory> = [];
    data.forEach((x, i) => {
      const existingItem = data.find((d) => d.category.id === x.category.id);
      if (existingItem) {
        const resultItem = results.find((d) => d.category.id === x.category.id);
        if (!resultItem) {
          results.push(x);
        } else {
          const itemsToAdd = x.category.questions.filter((c) =>
            existingItem.category.questions.every(
              (item) => item.questionId !== c.questionId
            )
          );
          existingItem.category.questions = existingItem.category.questions.concat(
            itemsToAdd
          );
        }
      }
    });
  }

  private getCategoryFromAddEvent(event: any): LocalizedQuestionCategory {
    const addedItemId = +event.clone.id;
    const categoryId = this.getCategoryIdFromAddEvent(event);
    const category = { ...this.categories.find((x) => x.id === categoryId) };
    category.questions = [];
    if (this.addedItemIsCategory(event)) {
      this.getCategoryQuestions(category);
    } else {
      this.addQuestionToCategory(addedItemId, category);
    }
    return category;
  }

  private addedItemIsCategory(event: any): boolean {
    const categoryId = event.item.attributes.categoryId
      ? event.item.attributes.categoryId.value
      : undefined;
    return isNaN(categoryId);
  }

  private getCategoryIdFromAddEvent(event: any): number {
    const addedItemId = +event.clone.id;
    return this.addedItemIsCategory(event)
      ? addedItemId
      : +event.item.attributes.categoryId.value;
  }

  private updateSurveyCategoriesSortableOptions(
    item: string,
    SortableOptions: Options,
    isAddToPutArray = false
  ) {
    const putArray: Array<string> = SortableOptions.group['put'];
    const itemIndex = putArray.findIndex((x) => x === item);
    if (itemIndex === -1 && !isAddToPutArray) {
      return;
    } else if (itemIndex === -1) {
      putArray.push(item);
      return;
    }
    putArray.splice(itemIndex, 1);
  }

  private addQuestionToCategory(
    questionId: number,
    category: LocalizedQuestionCategory
  ) {
    const questionIndex = category.questions.findIndex(
      (cq) => cq.id === questionId && !cq.answers
    );
    if (questionIndex !== -1) {
      category.questions.splice(questionIndex, 1);
    }
    if (
      category.questions.findIndex((q) => q.questionId === questionId) !== -1
    ) {
      return;
    }

    this.questionsService.getLocalized(questionId).subscribe((q) => {
      const question: SurveyInfrastructure = this.mapAdminQuestionToSurveyInfrastructure(
        q,
        category.id
      );
      category.questions.splice(questionIndex, 0, question);
      this.ref.detectChanges();
    });
  }

  private mapAdminQuestionToSurveyInfrastructure(
    question: AdminQuestion,
    categoryId: number
  ): SurveyInfrastructure {
    return {
      questionId: question.id,
      name: question.name,
      isMandatory: true,
      categoryId: categoryId,
      answerType: question.answerType,
      answers: question.answers,
    };
  }

  private mapSurveyToFormGroup() {
    this.formMappingService.addAllLocalizedNameToFormGroup(
      this.surveyForm,
      this.partOfNameControlName,
      this.survey.name
    );
    this.formMappingService.addAllLocalizedNameToFormGroup(
      this.surveyForm,
      this.partOfDescriptionName,
      this.survey.description
    );
    this.surveyForm.controls[this.durationControlName].setValue(
      this.survey.duration
    );
    this.namePropertiesArray = this.formMappingService.mapFormGroupControlToPropArray(
      this.surveyForm,
      this.partOfNameControlName
    );
    this.descriptionPropertiesArray = this.formMappingService.mapFormGroupControlToPropArray(
      this.surveyForm,
      this.partOfDescriptionName
    );
    this.surveyDraggableCategories = this.sortCategoryByQuestionsOrder(
      this.surveyDraggableCategories
    );
    this.setPlaceholders();
  }

  private sortCategoryByQuestionsOrder(
    draggableCategories: Array<DraggableCategory>
  ): Array<DraggableCategory> {
    let orderedQuestions: Array<SurveyInfrastructure> = [];
    draggableCategories.forEach(
      (x) => (orderedQuestions = orderedQuestions.concat(x.category.questions))
    );
    this.sortQuestionByOrder(orderedQuestions);
    const orderedCategoriesId = Array.from(
      new Set(orderedQuestions.map((x) => x.categoryId))
    );
    const orderedCategories = [];
    orderedCategoriesId.forEach((x) =>
      draggableCategories.forEach((c) => {
        if (c.category.id === x) {
          orderedCategories.push(c);
        }
      })
    );
    return orderedCategories;
  }

  private setPlaceholders() {
    const controlsNames = this.formMappingService.getAllFormControlsFields(
      this.surveyForm.controls
    );
    const nameFieldsControls = controlsNames.filter((x) =>
      x.includes(this.partOfNameControlName)
    );
    this.namePlaceholders = this.formMappingService.setPlaceholderArrayByPropName(
      nameFieldsControls,
      'Survey name'
    );
    const descriptionFieldsControls = controlsNames.filter((x) =>
      x.includes(this.partOfDescriptionName)
    );
    this.descriptionPlaceholders = this.formMappingService.setPlaceholderArrayByPropName(
      descriptionFieldsControls,
      'Survey description'
    );
  }
}
